/**
 * Validaciones previas a la escritura (Req. 18.6).
 *
 * Se ejecutan ANTES de abrir la transaccion. Rechazar un dato mal formado en la
 * frontera es mas barato que descubrirlo cuando ya contamino el inventario, y
 * evita transacciones abortadas a medias.
 */

import { ErrorValidacion } from '@/domain/errors';
import { esFechaLocal, type FechaLocal } from '@/domain/dates';
import { esCentavos, type Centavos } from '@/domain/money';
import type { Table } from 'dexie';

// ---------------------------------------------------------------------------
// Texto
// ---------------------------------------------------------------------------

export function exigirTexto(
  valor: unknown,
  campo: string,
  opciones: { minimo?: number; maximo?: number } = {},
): string {
  if (typeof valor !== 'string') {
    throw new ErrorValidacion(`El campo "${campo}" debe ser texto.`, campo);
  }
  const limpio = valor.trim();
  const minimo = opciones.minimo ?? 1;
  const maximo = opciones.maximo ?? 200;

  if (limpio.length < minimo) {
    throw new ErrorValidacion(
      minimo === 1
        ? `El campo "${campo}" no puede quedar vacio.`
        : `El campo "${campo}" debe tener al menos ${minimo} caracteres.`,
      campo,
    );
  }
  if (limpio.length > maximo) {
    throw new ErrorValidacion(
      `El campo "${campo}" no puede pasar de ${maximo} caracteres.`,
      campo,
    );
  }
  return limpio;
}

export function textoOpcional(valor: unknown, campo: string, maximo = 2000): string | null {
  if (valor === null || valor === undefined || valor === '') return null;
  if (typeof valor !== 'string') {
    throw new ErrorValidacion(`El campo "${campo}" debe ser texto.`, campo);
  }
  const limpio = valor.trim();
  if (limpio === '') return null;
  if (limpio.length > maximo) {
    throw new ErrorValidacion(`El campo "${campo}" no puede pasar de ${maximo} caracteres.`, campo);
  }
  return limpio;
}

// ---------------------------------------------------------------------------
// Numeros
// ---------------------------------------------------------------------------

export function exigirNumero(
  valor: unknown,
  campo: string,
  opciones: { minimo?: number; maximo?: number } = {},
): number {
  if (typeof valor !== 'number' || !Number.isFinite(valor)) {
    throw new ErrorValidacion(`El campo "${campo}" debe ser un numero valido.`, campo);
  }
  if (opciones.minimo !== undefined && valor < opciones.minimo) {
    throw new ErrorValidacion(
      `El campo "${campo}" no puede ser menor que ${opciones.minimo}.`,
      campo,
    );
  }
  if (opciones.maximo !== undefined && valor > opciones.maximo) {
    throw new ErrorValidacion(
      `El campo "${campo}" no puede ser mayor que ${opciones.maximo}.`,
      campo,
    );
  }
  return valor;
}

export function exigirEntero(
  valor: unknown,
  campo: string,
  opciones: { minimo?: number; maximo?: number } = {},
): number {
  const numero = exigirNumero(valor, campo, opciones);
  if (!Number.isInteger(numero)) {
    throw new ErrorValidacion(`El campo "${campo}" debe ser un numero entero.`, campo);
  }
  return numero;
}

/** Cantidad mayor que cero. Para lo que se compra, se produce o se vende. */
export function exigirCantidadPositiva(valor: unknown, campo: string): number {
  const numero = exigirNumero(valor, campo, { minimo: 0 });
  if (numero <= 0) {
    throw new ErrorValidacion(`El campo "${campo}" debe ser mayor que cero.`, campo);
  }
  return numero;
}

/** Cantidad de cero en adelante. Para existencias y minimos. */
export function exigirCantidadNoNegativa(valor: unknown, campo: string): number {
  return exigirNumero(valor, campo, { minimo: 0 });
}

/** Cantidad con signo. Para ajustes de inventario, que pueden ser negativos. */
export function exigirCantidadConSigno(valor: unknown, campo: string): number {
  const numero = exigirNumero(valor, campo);
  if (numero === 0) {
    throw new ErrorValidacion(`El campo "${campo}" no puede ser cero.`, campo);
  }
  return numero;
}

// ---------------------------------------------------------------------------
// Dinero
// ---------------------------------------------------------------------------

export function exigirImporte(valor: unknown, campo: string): Centavos {
  if (!esCentavos(valor)) {
    throw new ErrorValidacion(
      `El campo "${campo}" debe ser un importe valido en centavos enteros.`,
      campo,
    );
  }
  return valor;
}

export function exigirImporteNoNegativo(valor: unknown, campo: string): Centavos {
  const centavos = exigirImporte(valor, campo);
  if (centavos < 0) {
    throw new ErrorValidacion(`El campo "${campo}" no puede ser negativo.`, campo);
  }
  return centavos;
}

export function importeOpcional(valor: unknown, campo: string): Centavos | null {
  if (valor === null || valor === undefined) return null;
  return exigirImporteNoNegativo(valor, campo);
}

// ---------------------------------------------------------------------------
// Fechas
// ---------------------------------------------------------------------------

export function exigirFecha(valor: unknown, campo: string): FechaLocal {
  if (!esFechaLocal(valor)) {
    throw new ErrorValidacion(
      `El campo "${campo}" debe ser una fecha valida con formato AAAA-MM-DD.`,
      campo,
    );
  }
  return valor;
}

// ---------------------------------------------------------------------------
// Enumeraciones y referencias
// ---------------------------------------------------------------------------

export function exigirUnoDe<T extends string>(
  valor: unknown,
  opciones: readonly T[],
  campo: string,
): T {
  if (typeof valor !== 'string' || !opciones.includes(valor as T)) {
    throw new ErrorValidacion(
      `El campo "${campo}" debe ser uno de: ${opciones.join(', ')}.`,
      campo,
    );
  }
  return valor as T;
}

export function exigirBooleano(valor: unknown, campo: string): boolean {
  if (typeof valor !== 'boolean') {
    throw new ErrorValidacion(`El campo "${campo}" debe ser verdadero o falso.`, campo);
  }
  return valor;
}

/**
 * Verifica que un registro referenciado exista (Req. 18.6).
 * Evita renglones huerfanos que despues romperian los reportes.
 */
export async function exigirExiste<T, K>(
  tabla: Table<T, K>,
  id: K,
  etiqueta: string,
): Promise<T> {
  const registro = await tabla.get(id);
  if (registro === undefined) {
    throw new ErrorValidacion(`No se encontro ${etiqueta} con identificador "${String(id)}".`);
  }
  return registro;
}
